import { Box, Button, Grid2, Tooltip, Typography } from '@mui/material'
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent'
import CustomDivider from '../../shared/utils/CustomDivider'
import { useFacilityList } from '../../assets/api/useAdminApi'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useToaster } from '../../shared/utils/ToasterProvider'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TextFormField } from '../../shared/components/formfields/TextFormField'
import { Grid_Gap, Input_Width } from '../../shared/constants'
import useAuthStore from '../../store/useAuthStore'
import useFacilityStore from '../../store/useFacilityStore'
import api from '../../assets/nodeApi/axiosInstance'
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate'
import { GridActionsCellItem } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit';


const FacilityList = () => {

  const { data: facilityList, isLoading } = useFacilityList()
  const { facility, clearFacility } = useFacilityStore();
  const { setFacility } = useFacilityStore();
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  const { token } = useAuthStore()

  const facilityRows = facilityList?.map((item, index) => ({
    id: item?.id || index,
    sr_no: index + 1,
    ...item
  })) || [];


  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Facility Name is Required")
      .test(
        "duplicate-check",
        "This facility name is already taken",
        function (value) {
          const existingNames = facilityRows
            .filter(item => item.id !== facility?.id)
            .map(item => item.name.toLowerCase());

          return !existingNames.includes(value?.toLowerCase());
        }
      ),
    description: Yup.string().required("Descrition is Required"),
  });

  const initialValues = {
    name: facility?.name || "",
    description: facility?.description || ""
  };


  const mutation = useMutation({
    mutationFn: (formData) => {
      return facility?.id
        ? api.put(`facilities/${facility?.id}`, formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        : api.post(`facilities`, formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
    },
    onSuccess: (response) => {
      showToaster('Success', response?.data?.message, 'success')
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
    },
    onError: () => {
      showToaster("Error Caught While submiting the form", 'Error', 'error');
    },
  });

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    mutation.mutate(values, {
      onSettled: () => {
        setSubmitting(false);
      },
      onSuccess: () => {
        resetForm();
        clearFacility()
      }
    });

  }

  const facilityColumns = [
    {
      field: "sr_no",
      headerName: "Sr No",
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>

    },
    {
      field: "name",
      headerName: "Facility Name",
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{params.value} </Typography>,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      cellClassName: "actions",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Tooltip title="Edit">
            <GridActionsCellItem
              key={0}
              icon={<EditIcon />}
              label="Edit"
              onClick={() => {
                const selectedFacility = facilityRows?.find(row => row?.id === params?.id);
                if (selectedFacility) {
                  setFacility(selectedFacility);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            />
          </Tooltip>

        </Box>
      ),
    }

  ];

  return (
    <Box>

      <CustomDivider />
      <NormalContainerWithHeader headerText={"Facility List"}>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ resetForm }) => (
            <Form>
              <Grid2 container spacing={Grid_Gap}>
                <Grid2 children size={Input_Width}>
                  <Field
                    name="name"
                    component={TextFormField}
                    label="Enter Facility"
                    isRequired={true}
                  />
                </Grid2>
                <Grid2 children size={Input_Width}>
                  <Field
                    name="description"
                    component={TextFormField}
                    label="Add Description"
                    isRequired={true}
                  />
                </Grid2>

              </Grid2>
              <CustomDivider />
              <Grid2 container spacing={Grid_Gap} sx={{ display: "flex", justifyContent: "end" }}>
                <Grid2 children size={{ xs: 12, sm: 3 }}>
                  <Button variant='outlined' fullWidth
                    onClick={() => { resetForm(); clearFacility(); }}
                  >
                    Reset
                  </Button>
                </Grid2>
                <Grid2 children size={{ xs: 12, sm: 3 }}>
                  <Button variant='contained' type="submit" fullWidth>
                    {Object.keys(facility).length === 0 ? "Submit" : "Update"}
                  </Button>
                </Grid2>
              </Grid2>
            </Form>
          )}
        </Formik>
        <CustomDivider />
      </NormalContainerWithHeader>
      <CustomDivider orientation='horizontal' />
      <TableFilterTemplate
        title="Facility List"
        columns={facilityColumns}
        rowsData={facilityRows || []}
        isLoading={isLoading}
      />

    </Box>
  )
}

export default FacilityList